/**
 * Auth-endpoint wrappers (BUILD_SPEC.md §6.2).
 *
 * Thin adapters over the axios client so React components don't import
 * axios types directly. Each function returns the unwrapped JSON body
 * shape the API documents in §6.2.
 */

import { api } from "./client";
import type { AuthUser } from "@/stores/authStore";

interface LoginResponse {
  accessToken: string;
  user: AuthUser;
}

/** POST /api/auth/login. Throws on 4xx/5xx (axios default). */
export async function loginRequest(username: string, password: string): Promise<LoginResponse> {
  const res = await api.post<LoginResponse>("/auth/login", { username, password });
  return res.data;
}

/** GET /api/auth/me. Returns the freshest server view of the user. */
export async function meRequest(): Promise<AuthUser> {
  const res = await api.get<{ user: AuthUser }>("/auth/me");
  return res.data.user;
}

/**
 * POST /api/auth/logout. The backend revokes the refresh token AND
 * clears the cookie; we additionally clear the in-memory store on the
 * client. Errors are swallowed: a logout that the server fails to
 * acknowledge should still feel like "logged out" to the user.
 */
export async function logoutRequest(): Promise<void> {
  try {
    await api.post("/auth/logout");
  } catch {
    /* swallow — see fn doc */
  }
}
